/**
 * FamilyTreeNew — one-stop start-a-family-tree page.
 *
 * Two entry modes:
 *   • Blank tree     → user just clicks Create, backend seeds an empty tree
 *   • GEDCOM import  → user drops a .ged file from Ancestry / MyHeritage
 *                      / FamilySearch and the entire tree is imported in
 *                      one shot via /family-trees/gedcom/create-and-import
 *
 * Route: /family-trees/new  (?import=gedcom pre-selects the GEDCOM tab)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, TreePine, Upload, FileText, Info, Sparkles, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import TreeSketchPreview from "@/components/TreeSketchPreview";

export default function FamilyTreeNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialMode = params.get("import") === "gedcom" ? "gedcom" : "blank";
  const [mode, setMode] = useState(initialMode);

  useEffect(() => { document.title = "Start a Family Tree — Social Pulse"; }, []);

  // When the anonymous "Sketch my tree" widget's CTA is clicked, we
  // route to /auth so the visitor can sign in — and stash their prompt
  // in sessionStorage so the family-tree onboarding can pre-fill it
  // once they land back here signed-in.
  const handleSignupNudge = (draftPrompt) => {
    try {
      if (draftPrompt) sessionStorage.setItem("wofy:tree-sketch-draft", draftPrompt);
    } catch { /* private-mode / storage-off — harmless */ }
    nav(`/auth?next=${encodeURIComponent("/family-trees/new")}`);
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-6" data-testid="ftnew-anon-page">
        <div>
          <div className="overline text-xs">NEW FAMILY TREE</div>
          <h1 className="font-display text-3xl mt-1">Start a Family Tree</h1>
          <p className="text-sm opacity-70 mt-1">
            Try the sketch below to see how your tree will look — then sign
            in to save it and invite family.
          </p>
        </div>

        {/* Visitor-facing sample tree — a real, browsable public tree
            so the "here's what it looks like" question is answered
            instantly, without waiting for the LLM sketch to render. */}
        <Link
          to="/family/sample-heritage-family"
          data-testid="ftnew-sample-tree-anon"
          className="block mb-4 rounded-2xl border overflow-hidden group hover:shadow-lg transition-shadow"
          style={{ borderColor: "#E8DFCF", background: "linear-gradient(135deg, #FFF3D6 0%, #F6C583 60%, #C2410C 130%)" }}
        >
          <div className="flex items-center gap-4 px-4 sm:px-5 py-4 text-[#3D2A17]">
            <div className="w-12 h-12 rounded-full bg-white/70 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles className="w-6 h-6" style={{ color: "#B25D38" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-mono uppercase tracking-widest opacity-80">Sample tree — a fictional family</div>
              <div className="text-base sm:text-lg font-semibold">Explore The Sample Heritage Family</div>
              <div className="text-xs sm:text-sm opacity-80">5 generations · 17 members · 2 memorials · family message boards — hand-built to show you how a full IFT tree feels</div>
            </div>
            <ArrowRight className="w-5 h-5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
          </div>
          <img
            src="/ift/sample-tree-illustration.jpg"
            alt="Illustration of a full Indian Family Tree with three generations"
            className="w-full object-cover border-t"
            style={{ borderColor: "#E8DFCF", maxHeight: 240, objectPosition: "top" }}
            loading="lazy"
            data-testid="ftnew-tree-illustration-anon"
          />
        </Link>

        <TreeSketchPreview onSignupNudge={handleSignupNudge} />

        <div className="rounded-lg border p-4 text-center" style={{ borderColor: "var(--wofy-border)" }} data-testid="ftnew-signin-wall">
          <TreePine className="w-8 h-8 mx-auto opacity-30 mb-2" />
          <h2 className="font-display text-lg">Ready to build for real?</h2>
          <p className="mt-1 text-sm opacity-70">
            Sign in to save your tree, add photos and stories, and invite
            relatives to help fill in the missing branches.
          </p>
          <Link
            to={`/auth?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            className="inline-block mt-3 wofy-btn-primary"
            data-testid="ftnew-signin"
          >
            Sign in / Create account
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="ftnew-page">
      <div className="mb-6">
        <div className="overline text-xs">NEW FAMILY TREE</div>
        <h1 className="font-display text-3xl mt-1">Start a Family Tree</h1>
        <p className="text-sm opacity-70 mt-1">Pick how you'd like to begin. You can always add and edit later.</p>
      </div>

      {/* Visitor-facing sample tree — the "Sample Heritage Family" is
          a hand-built illustration owned by the IFT editorial team so
          the CTA never breaks even when other public trees go private.
          Marked ``is_demo=true`` so writes are rejected server-side. */}
      <Link
        to="/family/sample-heritage-family"
        data-testid="ftnew-sample-tree"
        className="block mb-6 rounded-2xl border overflow-hidden group hover:shadow-lg transition-shadow"
        style={{ borderColor: "#E8DFCF", background: "linear-gradient(135deg, #FFF3D6 0%, #F6C583 60%, #C2410C 130%)" }}
      >
        <div className="flex items-center gap-4 px-4 sm:px-5 py-4 text-[#3D2A17]">
          <div className="w-12 h-12 rounded-full bg-white/70 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Sparkles className="w-6 h-6" style={{ color: "#B25D38" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-mono uppercase tracking-widest opacity-80">Sample tree — a fictional family</div>
            <div className="text-base sm:text-lg font-semibold">Explore The Sample Heritage Family</div>
            <div className="text-xs sm:text-sm opacity-80">5 generations · 17 members · 2 memorials · family message boards</div>
          </div>
          <ArrowRight className="w-5 h-5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
        </div>
        <img
          src="/ift/sample-tree-illustration.jpg"
          alt="Illustration of a full Indian Family Tree with three generations"
          className="w-full object-cover border-t"
          style={{ borderColor: "#E8DFCF", maxHeight: 240, objectPosition: "top" }}
          loading="lazy"
          data-testid="ftnew-tree-illustration"
        />
      </Link>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          onClick={() => setMode("blank")}
          className={"rounded-lg border p-3 text-left transition " + (mode === "blank" ? "shadow-md ring-2" : "opacity-70")}
          style={{ borderColor: "var(--wofy-border)", background: mode === "blank" ? "#EFF6FF" : "#fff" }}
          data-testid="ftnew-mode-blank"
        >
          <TreePine className="w-5 h-5 mb-1" />
          <div className="font-semibold text-sm">Start blank</div>
          <div className="text-xs opacity-70">Add ancestors one at a time.</div>
        </button>
        <button
          onClick={() => setMode("gedcom")}
          className={"rounded-lg border p-3 text-left transition " + (mode === "gedcom" ? "shadow-md ring-2" : "opacity-70")}
          style={{ borderColor: "var(--wofy-border)", background: mode === "gedcom" ? "#F0FDF4" : "#fff" }}
          data-testid="ftnew-mode-gedcom"
        >
          <Upload className="w-5 h-5 mb-1" />
          <div className="font-semibold text-sm">Import GEDCOM</div>
          <div className="text-xs opacity-70">Ancestry / MyHeritage / FamilySearch export</div>
        </button>
      </div>

      {mode === "blank"  && <BlankForm nav={nav} />}
      {mode === "gedcom" && <GedcomForm nav={nav} />}
    </div>
  );
}


/* ------------------------------ blank ------------------------------ */
function BlankForm({ nav }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [country, setCountry] = useState("India");
  const [state, setState] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the tree a name."); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/family-trees", {
        name: name.trim(), description: description.trim(), visibility,
        country, origin_country: country, origin_state: state || undefined,
      });
      toast.success("Family tree created!");
      nav(`/family/${data.slug || data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create tree");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Family name (e.g. Vellaringattu Family)"
             className="w-full rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }}
             data-testid="ftnew-blank-name" required />
      <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional — a short note about this family"
                className="w-full rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }}
                data-testid="ftnew-blank-desc" />
      <div className="grid grid-cols-2 gap-2">
        <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country"
               className="rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }}
               data-testid="ftnew-blank-country" />
        <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State / region (optional)"
               className="rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }}
               data-testid="ftnew-blank-state" />
      </div>
      <select value={visibility} onChange={(e) => setVisibility(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--wofy-border)" }}
              data-testid="ftnew-blank-visibility">
        <option value="public">Public — anyone can view</option>
        <option value="invite">Invite-only — approved members</option>
        <option value="private">Private — admins only</option>
      </select>
      <button type="submit" disabled={busy} className="wofy-btn-primary w-full" data-testid="ftnew-blank-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TreePine className="w-4 h-4" />}
        Create tree
      </button>
    </form>
  );
}


/* ------------------------------ GEDCOM ------------------------------ */
function GedcomForm({ nav }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [country, setCountry] = useState("India");
  const [state, setState] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");

  const hint = useMemo(() => {
    if (!file) return "";
    const kb = Math.round(file.size / 1024);
    return `${file.name} · ${kb.toLocaleString()} KB`;
  }, [file]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a .ged file first."); return; }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("GEDCOM file must be under 5 MB. Export a smaller branch or split the file.");
      return;
    }
    if (!name.trim()) { toast.error("Give the tree a name so relatives can find it."); return; }
    setBusy(true);
    setProgress("Reading file…");
    try {
      const content = await file.text();
      setProgress("Uploading & importing — this may take a minute for large trees…");
      const { data } = await http.post("/family-trees/gedcom/create-and-import", {
        content, name: name.trim(),
        visibility, origin_country: country, origin_state: state || undefined,
      });
      toast.success(`Imported ${data.members_added} members and ${data.relationships_added} relationships.`);
      nav(`/family/${data.tree.slug || data.tree.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "GEDCOM import failed";
      toast.error(msg);
      setProgress("");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border p-4" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="rounded-lg p-3 text-xs flex gap-2 items-start" style={{ background: "#F0FDF4", color: "#166534" }}>
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <b>How to get a GEDCOM file:</b> On <b>Ancestry.com</b> → Trees → Manage Tree → Export Tree. On <b>MyHeritage</b> → Family Trees → Export as GEDCOM.
          On <b>FamilySearch</b> → Family Tree → Download GEDCOM. Save the `.ged` file and drop it here.
        </div>
      </div>

      <label className="block rounded-lg border-2 border-dashed p-6 text-center cursor-pointer hover:bg-slate-50 transition"
             style={{ borderColor: "var(--wofy-border)" }} data-testid="ftnew-gedcom-dropzone">
        <input type="file" accept=".ged,.gedcom,application/x-gedcom,text/plain"
               onChange={(e) => setFile(e.target.files?.[0] || null)}
               className="hidden" data-testid="ftnew-gedcom-file" />
        {file ? (
          <div>
            <FileText className="w-10 h-10 mx-auto mb-1" />
            <div className="font-semibold text-sm">{hint}</div>
            <div className="text-xs opacity-70 mt-1">Click to replace</div>
          </div>
        ) : (
          <div>
            <Upload className="w-10 h-10 mx-auto mb-1 opacity-40" />
            <div className="font-semibold text-sm">Drop your .ged file here</div>
            <div className="text-xs opacity-70 mt-1">…or click to browse (up to 5 MB)</div>
          </div>
        )}
      </label>

      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Family name (e.g. Nair Family)"
             className="w-full rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }}
             data-testid="ftnew-gedcom-name" required />
      <div className="grid grid-cols-2 gap-2">
        <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country"
               className="rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }}
               data-testid="ftnew-gedcom-country" />
        <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State (optional)"
               className="rounded-lg border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--wofy-border)" }}
               data-testid="ftnew-gedcom-state" />
      </div>
      <select value={visibility} onChange={(e) => setVisibility(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--wofy-border)" }}
              data-testid="ftnew-gedcom-visibility">
        <option value="public">Public</option>
        <option value="invite">Invite-only</option>
        <option value="private">Private</option>
      </select>
      <button type="submit" disabled={busy || !file} className="wofy-btn-primary w-full" data-testid="ftnew-gedcom-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {busy ? "Importing…" : "Import GEDCOM"}
      </button>
      {progress && <div className="text-xs opacity-70 text-center" data-testid="ftnew-gedcom-progress">{progress}</div>}
    </form>
  );
}
